import { assign, get, isArray, reduce, startCase } from 'lodash';

import { fileDownload } from '../../../../common/services/fileUtils';
import { setMessageExportUnavailable } from '../../../../common/components/Alert/actions';
import { setMessage } from '../../../../common/components/Notification/actions';

import {
  addOrUpdateItem,
  listRequest,
  listRequestError,
  listRequestSuccess,
  setTotalCount,
} from '../../../../common/components/SSPList/actions';
import dictionariesTypesEnum from '../../../../common/constants/dictionaries';
import {
  TELEMETRY_ACCURACY_DEFAULT_VALUE,
  TELEMETRY_MULTIPLICATOR_DEFAULT_VALUE,
} from '../../../../common/constants/telemetry';
import Telemetry from '../../../../common/resources/Telemetry';

import APIClient from '../../../../common/services/APIClient';

export const setMetrics = (metrics) => ({
  type: 'SET_TELEMETRY_METRICS',
  metrics,
});

export const loadDictionary =
  (id, { filter_by: filterBy = {}, sorting, page_size: pageSize = 20, page_token: pageToken = 0 } = {}) =>
  (dispatch) => {
    dispatch(listRequest(id));

    const params = {
      params: {
        page_token: pageToken,
        page_size: pageSize,
        sort_by: sorting ? sorting.id : 'code',
        sort_order: sorting && sorting.desc ? 'DESC' : 'ASC',
      },
    };

    const filter = reduce(filterBy, (result, item) => assign({}, result, { [item.id]: item.value }), {});

    return Telemetry.findAll(filter, { ...params })
      .then((resp) => {
        const payloads = get(resp, 'data.payloads', []);
        const size = get(resp, 'data.total_size', 0);

        dispatch(
          listRequestSuccess(
            id,
            payloads.map((metric) => ({ ...metric, code: metric.code.toUpperCase() })),
          ),
        );
        dispatch(setMetrics(payloads));
        dispatch(setTotalCount(id, size));
      })
      .catch((err) => {
        dispatch(listRequestError(id, err));
      });
  };

const handleResponseError =
  (reject) =>
  (error = {}) => {
    const { response } = error;
    let message = 'Something went wrong. Please contact your site administrator.';

    if (response) {
      const {
        status,
        data: { errors },
      } = response;

      if (errors && errors.length && status > 299 && status < 499) {
        const [err] = errors;

        if (err.field) {
          message = `${startCase(err.field)} ${err.message}`;
        } else {
          message = err.message;
        }
      } else if (response.data.message) {
        if (response.data.field) {
          message = `${startCase(response.data.field)} ${response.data.message}`;
        } else {
          message = response.data.message;
        }
      }
    }

    reject(message);
  };

function createAnUpdate(previousResult) {
  return new Promise((resolve, reject) => {
    this.data.title = this.data.title.trim();
    if (!this.data.accuracy || this.data.accuracy === '') {
      this.data.accuracy = TELEMETRY_ACCURACY_DEFAULT_VALUE;
    }
    if (!this.data.multiplicator || this.data.multiplicator === '') {
      this.data.multiplicator = TELEMETRY_MULTIPLICATOR_DEFAULT_VALUE;
    }
    const { uuid, ...queryParams } = this.data;

    if (!uuid) {
      Telemetry.create(queryParams).then(resolve).catch(handleResponseError(reject));
    } else {
      resolve(previousResult);
    }
  });
}

function editAnUpdate(previousResult) {
  return new Promise((resolve, reject) => {
    this.data.title = this.data.title.trim();
    if (!this.data.accuracy || this.data.accuracy === '') {
      this.data.accuracy = TELEMETRY_ACCURACY_DEFAULT_VALUE;
    }
    if (!this.data.multiplicator || this.data.multiplicator === '') {
      this.data.multiplicator = TELEMETRY_MULTIPLICATOR_DEFAULT_VALUE;
    }
    const { uuid, ...rest } = this.data;

    if (uuid) {
      Telemetry.update({ uuid }, { ...rest, uuid })
        .then(resolve)
        .catch(handleResponseError(reject));
    } else {
      resolve(previousResult);
    }
  });
}

export const telemetryMetricsRequestSuccess = () => ({
  type: 'TELEMETRY_METRICS_REQUEST_SUCCESS',
});

function handleSuccessResult(response) {
  const {
    config: { method },
    data: newItem,
  } = response;

  if (method === 'put' || method === 'post') {
    this.dispatch(telemetryMetricsRequestSuccess());
    this.dispatch(addOrUpdateItem(dictionariesTypesEnum.telemetryMetrics, newItem, 'uuid'));
  } else {
    throw Error('Undefined response');
  }
}

export const stopFetchingMetrics = () => ({
  type: 'STOP_FETCHING_TELEMETRY_METRICS',
});

function handleFailResult(message) {
  this.dispatch(setMessage({ message }));
  this.dispatch(stopFetchingMetrics());
}

const roundMultiplicator = (multiplicator, digitsCount) => {
  const delimiter = multiplicator.toString().includes('.') ? '.' : ',';
  const multiplicatorArray = multiplicator.toString().split(delimiter);
  const decimalPart = multiplicatorArray.length === 2 ? multiplicatorArray[1].length : 0;

  if (decimalPart > digitsCount) {
    if (multiplicatorArray[1].replaceAll('9', '').length === 0) {
      return Math.round(multiplicator);
    }
    const roundedValue = Math.floor(
      parseInt(multiplicatorArray[1], 10) / 10 ** (multiplicatorArray[1].length - digitsCount),
    );

    return parseFloat([multiplicatorArray[0], roundedValue].join(delimiter));
  }

  return multiplicator;
};

export const telemetryMetricsRequest = () => ({
  type: 'TELEMETRY_METRICS_REQUEST',
});

export const saveMetric = (formData) => (dispatch) => {
  dispatch(telemetryMetricsRequest());

  const data = {
    ...formData,
    multiplicator: roundMultiplicator(formData.multiplicator, 15),
  };

  return new Promise((resolve) => resolve())
    .then(editAnUpdate.bind({ data }))
    .then(createAnUpdate.bind({ data }))
    .then(handleSuccessResult.bind({ dispatch }))
    .catch(handleFailResult.bind({ dispatch }));
};

export const openEditDialog = (mode) => ({
  type: 'OPEN_TELEMETRY_METRICS_EDIT_DIALOG',
  mode,
});

export const closeEditDialog = () => ({
  type: 'CLOSE_TELEMETRY_METRICS_EDIT_DIALOG',
});

export const telemetryMetricsRequestError = (message) => ({
  type: 'TELEMETRY_METRICS_REQUEST_ERROR',
  message,
});

export const addToMetrics = (metric) => ({
  type: 'ADD_TO_TELEMETRY_METRICS',
  payload: metric,
});

export const toggleChecked = (metric) => ({
  type: 'TOGGLE_CHECKED_METRICS',
  metric,
});

export const toggleSelectAll = () => ({
  type: 'TOGGLE_SELECT_ALL_METRICS',
});

export const deselectAll = () => ({
  type: 'DESELECT_ALL_METRICS',
});

const defaultSort = { id: 'code', desc: false };

export const exportMetrics =
  (uuids = [], sorting) =>
  (dispatch) => {
    const payload = {
      filter_by: { uuids },
    };

    let tableSorting;

    if (isArray(sorting)) {
      tableSorting = sorting[0] || defaultSort;
    } else {
      tableSorting = sorting || defaultSort;
    }

    const params = {
      sort_by: tableSorting.id,
      sort_order: tableSorting.desc ? 'DESC' : 'ASC',
    };

    return APIClient.post('dictionaries/telemetry-metrics/export', payload, { params })
      .then(({ data, headers }) => {
        fileDownload(data, 'exported-telemetry-metrics.csv', headers['content-type']);
      })
      .catch((err) => {
        if (err.response && err.response.status === 500) {
          dispatch(setMessageExportUnavailable());
        }
        console.error(err);
      });
  };
